export const LAND_PARCEL_PROPERTY_TYPES = [
  "house",
  "land",
  "commercial",
  "industrial",
];

export const PARCEL_REQUIRED_FOR_ACTIVE_TYPES = [
  "house",
  "land",
  "industrial",
];

export const propertyAllowsLandParcels = (type) =>
  LAND_PARCEL_PROPERTY_TYPES.includes(type);

export const propertyRequiresParcelForActive = (type) =>
  PARCEL_REQUIRED_FOR_ACTIVE_TYPES.includes(type);

export const LAND_PURPOSE_OPTIONS = [
  { value: "residential", label: "Під житлову забудову" },
  { value: "agricultural", label: "Сільськогосподарського призначення" },
  { value: "commercial", label: "Комерційного використання" },
  { value: "industrial", label: "Промислового використання" },
  { value: "recreational", label: "Рекреаційне" },
  { value: "garden", label: "Садова / дачна" },
];

export const CADASTRAL_RE = /^\d{10}:\d{2}:\d{3}:\d{4}$/;

/** Optional сотки: digits with at most four fractional places. No JS unit math. */
export const AREA_SOTKY_RE = /^\d+(?:\.\d{1,4})?$/;

export const APARTMENT_TRANSITION_BLOCKED_MESSAGE =
  "Спочатку видаліть усі земельні ділянки, а потім змініть тип на квартиру.";

export const APARTMENT_PARCEL_WRITE_MESSAGE =
  "Земельні ділянки не можна додавати до квартири.";

export const GENERIC_PARCEL_SAVE_ERROR =
  "Не вдалося зберегти земельну ділянку. Спробуйте ще раз.";

export const ACTIVATION_PARCEL_REQUIRED_MESSAGE =
  "Для активації будинку, землі або промислової нерухомості "
  + "додайте щонайменше одну кадастрову ділянку.";

export const LAST_PARCEL_DELETE_BLOCKED_MESSAGE =
  "Не можна видалити останню кадастрову ділянку "
  + "активного оголошення цього типу.";

export const ACTIVE_TYPE_REQUIRES_PARCEL_MESSAGE =
  "Для активного оголошення цього типу потрібна "
  + "щонайменше одна кадастрова ділянка.";

export const apartmentTransitionBlockedMessage =
  APARTMENT_TRANSITION_BLOCKED_MESSAGE;

export const getApartmentTransitionBlockReason = (parcels, nextType) =>
  Array.isArray(parcels) && parcels.length > 0 && nextType === "apartment"
    ? APARTMENT_TRANSITION_BLOCKED_MESSAGE
    : null;

export const getParcelEditorGuidance = (propertyType) => {
  if (propertyType === "commercial") {
    return (
      "Додайте кожну кадастрову ділянку окремо. Для комерційної нерухомості "
      + "ділянки необов’язкові. Чернетку можна зберегти без ділянок."
    );
  }
  if (propertyRequiresParcelForActive(propertyType)) {
    return (
      "Додайте кожну кадастрову ділянку окремо. Чернетку можна зберегти "
      + "без ділянок; для активації потрібна щонайменше одна ділянка."
    );
  }
  return "Додайте кожну кадастрову ділянку окремо.";
};

const isPositiveSotkyString = (value) => {
  if (!AREA_SOTKY_RE.test(value)) return false;
  // Reject all-zero without Number() unit conversion.
  return /[1-9]/.test(value);
};

export const validateParcelDraft = (draft = {}) => {
  const errors = {};
  const cadastralNumber = String(draft.cadastral_number ?? "").trim();
  const purposeCategory = String(draft.purpose_category ?? "").trim();
  const area = String(draft.area_sotky ?? "").trim();

  if (!cadastralNumber) {
    errors.cadastral_number = "Введіть кадастровий номер.";
  } else if (!CADASTRAL_RE.test(cadastralNumber)) {
    errors.cadastral_number =
      "Кадастровий номер має формат 2610100000:01:001:0001.";
  }

  if (!LAND_PURPOSE_OPTIONS.some((option) => option.value === purposeCategory)) {
    errors.purpose_category = "Оберіть категорію цільового призначення.";
  }

  if (area) {
    if (!AREA_SOTKY_RE.test(area)) {
      if (/^\d+\.\d{5,}$/.test(area)) {
        errors.area_sotky =
          "Площа в сотках може містити не більше чотирьох знаків після коми.";
      } else {
        errors.area_sotky = "Введіть коректну площу.";
      }
    } else if (!isPositiveSotkyString(area)) {
      errors.area_sotky = "Площа має бути більшою за нуль.";
    }
  }

  return { ok: Object.keys(errors).length === 0, errors };
};

export const buildParcelWritePayload = (draft = {}) => {
  const area = String(draft.area_sotky ?? "").trim();
  return {
    cadastral_number: String(draft.cadastral_number ?? "").trim(),
    purpose_category: String(draft.purpose_category ?? "").trim(),
    area_sotky: area || null,
    has_red_lines_or_restrictions: Boolean(draft.has_red_lines_or_restrictions),
  };
};

const firstMessage = (value) => {
  if (typeof value === "string") return value;
  if (Array.isArray(value)) {
    return value.find((item) => typeof item === "string") || "";
  }
  return "";
};

const mapAreaApiMessage = (raw) => {
  const lower = String(raw || "").toLowerCase();
  if (/чотирьох знаків|four decimal|decimal places|too many decimal/.test(lower)) {
    return "Площа в сотках може містити не більше чотирьох знаків після коми.";
  }
  if (/занадто велик|too large|max.?digits|whole digits/.test(lower)) {
    return "Значення площі занадто велике.";
  }
  if (
    /більшою за нуль|greater than 0|must be greater|positive|negative|нуль/.test(
      lower,
    )
  ) {
    return "Площа має бути більшою за нуль.";
  }
  if (/округлен|representable|truncat|round/.test(lower)) {
    return "Значення площі не може бути збережене без округлення.";
  }
  if (/decimal|number|коректн|invalid/.test(lower)) {
    return "Введіть коректну площу.";
  }
  return "Введіть коректну площу.";
};

const mapPropertyTypeFormMessage = (raw) => {
  const text = String(raw || "");
  if (/спочатку видаліть|змініть тип на квартиру/i.test(text)) {
    return APARTMENT_TRANSITION_BLOCKED_MESSAGE;
  }
  if (/активного оголошення цього типу потрібна/i.test(text)) {
    return ACTIVE_TYPE_REQUIRES_PARCEL_MESSAGE;
  }
  return APARTMENT_PARCEL_WRITE_MESSAGE;
};

const mapFormMessage = (raw) => {
  const text = String(raw || "");
  if (/останню кадастрову ділянку/i.test(text)) {
    return LAST_PARCEL_DELETE_BLOCKED_MESSAGE;
  }
  if (/для активації будинку/i.test(text)) {
    return ACTIVATION_PARCEL_REQUIRED_MESSAGE;
  }
  if (/активного оголошення цього типу потрібна/i.test(text)) {
    return ACTIVE_TYPE_REQUIRES_PARCEL_MESSAGE;
  }
  return GENERIC_PARCEL_SAVE_ERROR;
};

export const mapParcelApiErrors = (error) => {
  const data = error && error.response && error.response.data;
  const status = error && error.response && error.response.status;

  if (status === 403) {
    return { _form: "Лише власник оголошення може змінювати земельні ділянки." };
  }
  if (status === 404) {
    return { _form: "Земельну ділянку або оголошення не знайдено." };
  }
  if (!data || typeof data !== "object" || Array.isArray(data)) {
    return { _form: GENERIC_PARCEL_SAVE_ERROR };
  }

  const mapped = {};
  const cadastralMessage = firstMessage(data.cadastral_number).toLowerCase();
  if (cadastralMessage) {
    if (/duplicate|unique|already|існу|дубл|уже додано/.test(cadastralMessage)) {
      mapped.cadastral_number = "Цей кадастровий номер уже додано до об’єкта.";
    } else if (/довг|max.?length|too long/.test(cadastralMessage)) {
      mapped.cadastral_number = "Кадастровий номер занадто довгий.";
    } else if (/введіть кадастровий|required|blank|null/.test(cadastralMessage)) {
      mapped.cadastral_number = "Введіть кадастровий номер.";
    } else {
      mapped.cadastral_number =
        "Кадастровий номер має формат 2610100000:01:001:0001.";
    }
  }

  const purposeCategoryMessage = firstMessage(data.purpose_category);
  if (purposeCategoryMessage) {
    mapped.purpose_category = "Оберіть категорію цільового призначення.";
  }

  const areaMessage = firstMessage(data.area_sotky);
  if (areaMessage) {
    mapped.area_sotky = mapAreaApiMessage(areaMessage);
  }

  if (Object.prototype.hasOwnProperty.call(data, "has_red_lines_or_restrictions")) {
    mapped.has_red_lines_or_restrictions =
      "Оберіть, чи є червоні лінії або інші обмеження.";
  }

  // Never echo raw property_type / detail / non_field_errors / status text.
  if (firstMessage(data.property_type)) {
    mapped._form = mapPropertyTypeFormMessage(firstMessage(data.property_type));
  } else if (firstMessage(data.status)) {
    mapped._form = mapFormMessage(firstMessage(data.status));
  } else if (
    firstMessage(data.non_field_errors)
    || firstMessage(data.detail)
  ) {
    mapped._form = mapFormMessage(
      firstMessage(data.non_field_errors) || firstMessage(data.detail),
    );
  }

  if (Object.keys(mapped).length === 0) {
    mapped._form = GENERIC_PARCEL_SAVE_ERROR;
  }
  return mapped;
};
